import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { estagioService } from '@/services/api';
import {
  ArrowLeftIcon,
  PencilIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

export const VerEstagioPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: estagio, isLoading, error } = useQuery({
    queryKey: ['estagio', id],
    queryFn: () => estagioService.getById(Number(id!)),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados do estágio...</p>
        </div>
      </div>
    );
  }

  if (error || !estagio) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-red-600">Erro ao carregar dados do estágio</p>
          <Button
            onClick={() => navigate('/estagios')}
            className="mt-4"
          >
            Voltar para Lista
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Não informado';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value?: number) => {
    if (!value) return 'Não informado';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo':
        return 'bg-green-100 text-green-800';
      case 'Concluido':
        return 'bg-blue-100 text-blue-800';
      case 'Cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'Obrigatorio':
        return 'bg-purple-100 text-purple-800';
      case 'NaoObrigatorio':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/estagios')}
            className="flex items-center space-x-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Voltar</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Estágio #{estagio.id}
            </h1>
            <p className="text-sm text-gray-500">
              {estagio.aluno?.nome} - {estagio.empresa?.nome}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Link to={`/estagios/${estagio.id}/editar`}>
            <Button className="flex items-center space-x-2">
              <PencilIcon className="h-4 w-4" />
              <span>Editar</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Status e Tipo */}
      <div className="flex items-center space-x-4">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(estagio.status)}`}>
          {estagio.status}
        </span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoColor(estagio.tipo)}`}>
          {estagio.tipo === 'Obrigatorio' ? 'Obrigatório' : 'Não Obrigatório'}
        </span>
        {estagio.remunerado && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Remunerado
          </span>
        )}
      </div>

      {/* Informações Principais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dados do Estagiário */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <UserIcon className="h-5 w-5" />
              <span>Dados do Estagiário</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Nome</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">RG</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.rg || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">CPF</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.cpf || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Email</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.email || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Telefone</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.telefone || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Dados da Empresa */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BuildingOfficeIcon className="h-5 w-5" />
              <span>Dados da Empresa</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Razão Social</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Nome Fantasia</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.nomeFantasia || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">CNPJ</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.cnpj || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Representante</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.representante || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Email</Label>
              <p className="text-sm text-gray-900">{estagio.empresa?.email || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Informações Acadêmicas e Financeiras */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dados Acadêmicos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AcademicCapIcon className="h-5 w-5" />
              <span>Dados Acadêmicos</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Instituição</Label>
              <p className="text-sm text-gray-900">{estagio.instituicao?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Curso</Label>
              <p className="text-sm text-gray-900">{estagio.curso?.nome || estagio.aluno?.curso?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Origem da Instituição</Label>
              <p className="text-sm text-gray-900">{estagio.origemInstituicao || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Série/Período</Label>
              <p className="text-sm text-gray-900">{estagio.aluno?.serie || 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Dados Financeiros */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CurrencyDollarIcon className="h-5 w-5" />
              <span>Dados Financeiros</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Bolsa Auxílio</Label>
              <p className="text-sm text-gray-900">{formatCurrency(estagio.bolsaAuxilio)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Remunerado</Label>
              <p className="text-sm text-gray-900">{estagio.remunerado ? 'Sim' : 'Não'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Carga Horária Semanal</Label>
              <p className="text-sm text-gray-900">{estagio.cargaHorariaSemanal ? `${estagio.cargaHorariaSemanal}h` : 'Não informado'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Informações de Período e Seguro */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Período do Estágio */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5" />
              <span>Período do Estágio</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de Início</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.dataInicio)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de Término</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.dataTermino)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de Assinatura</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.dataAssinatura)}</p>
            </div>
            {estagio.dataCancelamento && (
              <div>
                <Label className="text-sm font-medium text-gray-500">Data de Cancelamento</Label>
                <p className="text-sm text-gray-900">{formatDate(estagio.dataCancelamento)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seguro */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ShieldCheckIcon className="h-5 w-5" />
              <span>Seguro</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Seguradora</Label>
              <p className="text-sm text-gray-900">{estagio.seguradora?.nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Apólice</Label>
              <p className="text-sm text-gray-900">{estagio.seguroApolice || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Possui Responsável (Menor)</Label>
              <p className="text-sm text-gray-900">{estagio.possuiResponsavelMenor ? 'Sim' : 'Não'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Informações Adicionais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <DocumentTextIcon className="h-5 w-5" />
            <span>Informações Adicionais</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Data de Criação</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.createdAt)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Última Atualização</Label>
              <p className="text-sm text-gray-900">{formatDate(estagio.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
